import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useUiLocale } from "../context/UiLocaleContext";
import { useWizard } from "../context/WizardContext";
import { useTranslation } from "../i18n/useTranslation";
import Modal from "./Wizard/Modal";
import PlatformLanguageSwitcher from "./PlatformLanguageSwitcher";
import { API_BASE_URL } from "../config/apiBaseUrl";
import { HOME_DRAWER_SIDEBAR_URL } from "../regularUserBackgroundPreload";

const HEADER_LOGO =
  "https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/home/logo.png";

const DRAWER_PANEL_ID = "header-nav-drawer-panel";

function scrollToSectionId(sectionId: string) {
  const el = document.getElementById(sectionId);
  if (!el || typeof window === "undefined") return;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
}

type DrawerNavItemProps = {
  label: React.ReactNode;
  onActivate: () => void;
  icon: React.ReactNode;
};

function DrawerMenuIconSparkle() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" aria-hidden>
      <path d="M12 4v16M8 12h8M7.76 7.76l8.48 8.48M15.52 8.48l-8.48 8.48" />
    </svg>
  );
}

function DrawerMenuIconFlow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9.25" />
      {/* Recognizable “?”: hook from Feather help-circle + stem + dot */}
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 14.15V16.7" />
      <circle cx="12" cy="18.52" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function DrawerMenuIconRocket() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {/* Fork: single road down, splitting into two */}
      <path d="M12 4v11" />
      <path d="m12 15-6 7.5M12 15l6 7.5" />
    </svg>
  );
}

function DrawerMenuIconPictureFrame() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4.25" y="4.25" width="15.5" height="15.5" rx="1.25" />
      <rect x="7.65" y="7.65" width="8.7" height="8.7" rx="0.4" />
    </svg>
  );
}

function DrawerMenuIconHeart() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20.84 12.61a5.5 5.5 0 0 0-7.79-7.76l-.05.05-.05-.05a5.49 5.49 0 0 0-7.74 7.74l8.53 8.53 8.1-8.51z" />
    </svg>
  );
}

function DrawerNavRow({ label, onActivate, icon }: DrawerNavItemProps) {
  return (
    <button
      type="button"
      onClick={onActivate}
      dir="auto"
      className="flex w-full items-center gap-3 rounded-2xl border border-[#E6E0D8] bg-[#FFF7F3] px-4 py-3.5 text-start text-sm font-semibold leading-tight text-[#2B2B2B] shadow-[0_8px_20px_rgba(0,0,0,0.06)] transition-all duration-200 ease-out hover:scale-[1.02] hover:border-[#6FA8DC] hover:bg-gradient-to-br hover:from-[#EEF5FC] hover:to-[#FDF3EC] hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)] motion-reduce:hover:scale-100 active:border-[#6FA8DC] active:bg-gradient-to-br active:from-[#EEF5FC] active:to-[#FDF3EC] active:shadow-[0_12px_28px_rgba(0,0,0,0.08)] motion-reduce:active:scale-100 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6FA8DC]/40 focus-visible:outline-offset-2"
    >
      <span className="inline-flex shrink-0 text-brandOrange [&>svg]:h-5 [&>svg]:w-5 [&>svg]:shrink-0 [&>svg]:opacity-[0.85]" aria-hidden>
        {icon}
      </span>
      <span className="min-w-0 flex-1">{label}</span>
    </button>
  );
}

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { token, user, logout, isLoading, refreshUser } = useAuth();
  const { resetWizard } = useWizard();
  const { t } = useTranslation();
  const { uiLocale } = useUiLocale();

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isClearSubmitting, setIsClearSubmitting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  const [heroIntroReady, setHeroIntroReady] = useState(false);
  const [magicVideoOpen, setMagicVideoOpen] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);

  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setReduceMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    const onHeroComplete = () => setHeroIntroReady(true);
    window.addEventListener("mysipur:hero-fade-complete", onHeroComplete);
    const fallbackTimer = window.setTimeout(() => setHeroIntroReady(true), 1200);
    return () => {
      window.removeEventListener("mysipur:hero-fade-complete", onHeroComplete);
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  useEffect(() => {
    const onOpen = () => setMagicVideoOpen(true);
    const onClose = () => setMagicVideoOpen(false);
    window.addEventListener("mysipur:hero-magic-video-open", onOpen);
    window.addEventListener("mysipur:hero-magic-video-close", onClose);
    return () => {
      window.removeEventListener("mysipur:hero-magic-video-open", onOpen);
      window.removeEventListener("mysipur:hero-magic-video-close", onClose);
    };
  }, []);

  useEffect(() => {
    if (!navDrawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [navDrawerOpen]);

  useEffect(() => {
    if (!navDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setNavDrawerOpen(false);
        window.setTimeout(() => menuBtnRef.current?.focus(), 0);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navDrawerOpen]);

  useEffect(() => {
    if (!navDrawerOpen) return;
    const id = window.requestAnimationFrame(() => drawerCloseRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [navDrawerOpen]);

  const closeDrawerAndFocusMenu = useCallback(() => {
    setNavDrawerOpen(false);
    window.setTimeout(() => menuBtnRef.current?.focus(), 0);
  }, []);

  const showChrome = heroIntroReady && !magicVideoOpen;
  const headerMotion = reduceMotion
    ? "transition-opacity duration-150 ease-out"
    : "transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]";
  const headerVisibility = showChrome
    ? "translate-y-0 opacity-100"
    : reduceMotion
      ? "opacity-0 pointer-events-none"
      : "-translate-y-[110%] opacity-0 pointer-events-none";

  const handleSeeTheMagic = useCallback(() => {
    window.dispatchEvent(new CustomEvent("mysipur:open-hero-magic"));
  }, []);

  const drawerGoToSection = useCallback(
    (sectionId: string) => {
      closeDrawerAndFocusMenu();
      window.setTimeout(() => scrollToSectionId(sectionId), 0);
    },
    [closeDrawerAndFocusMenu]
  );

  const drawerSeeMagic = useCallback(() => {
    handleSeeTheMagic();
    closeDrawerAndFocusMenu();
  }, [handleSeeTheMagic, closeDrawerAndFocusMenu]);

  const handleLogout = () => {
    resetWizard();
    logout();
    navigate("/", { replace: true });
  };

  const handleAdminSubmissionsClick = () => {
    navigate("/admin/submissions");
  };

  const handleClearSessionClick = () => {
    if (!token) return;
    setErrorModalOpen(false);
    setErrorModalMessage("");
    setIsClearConfirmOpen(true);
  };

  const handleClearSessionConfirm = async () => {
    if (isClearSubmitting) return;
    setIsClearSubmitting(true);

    try {
      const resp = await fetch(`${API_BASE_URL}/auth/clear-current-session`, {
        method: "POST",
      }).catch(() => null);

      const data = await resp?.json().catch(() => null);
      const ok = !!resp?.ok && !!data?.success;
      if (!ok) {
        setErrorModalMessage(data?.message || t("header.clearSessionErrorFallback"));
        setErrorModalOpen(true);
        return;
      }

      resetWizard();
      await refreshUser();
      navigate("/create/language?startFresh=1", { replace: true });
    } finally {
      setIsClearSubmitting(false);
      setIsClearConfirmOpen(false);
    }
  };

  const drawer =
    typeof document !== "undefined" && navDrawerOpen
      ? createPortal(
          <div className="fixed inset-0 z-[100]" dir="ltr">
            <button
              type="button"
              aria-label={t("header.closeDrawerAriaLabel")}
              className="absolute inset-0 z-0 h-full w-full cursor-default border-0 bg-brandNavy/45 p-0 backdrop-blur-[2px]"
              onClick={closeDrawerAndFocusMenu}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="header-drawer-title"
              id={DRAWER_PANEL_ID}
              className="absolute inset-y-0 start-0 z-[1] flex w-[min(20.5rem,86vw)] max-w-[100vw] flex-col overflow-hidden border-e border-[#E6E0D8] bg-[#F3EFE8] shadow-[8px_0_40px_-12px_rgba(43,43,43,0.12)]"
              style={{
                backgroundImage: `url('${HOME_DRAWER_SIDEBAR_URL}')`,
                backgroundSize: "cover",
                backgroundPosition: "center top",
                backgroundRepeat: "no-repeat",
              }}
            >
              <div className="relative z-[1] flex shrink-0 items-center justify-between gap-3 border-b border-[#E6E0D8]/80 bg-white/30 px-4 py-3 backdrop-blur-[2px]">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <img
                    src={HEADER_LOGO}
                    alt=""
                    aria-hidden
                    className="h-9 w-auto shrink-0 translate-y-1 object-contain drop-shadow-[0_1px_2px_rgba(43,43,43,0.12)] sm:h-10 sm:translate-y-1.5"
                    width={40}
                    height={40}
                  />
                  <h2 id="header-drawer-title" className="min-w-0 truncate text-base font-extrabold tracking-tight text-[#2B2B2B]">
                    MySipur
                  </h2>
                </div>
                <button
                  ref={drawerCloseRef}
                  type="button"
                  onClick={closeDrawerAndFocusMenu}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E6E0D8] bg-[#FFFFFF] text-[#6B6B6B] shadow-[0_4px_12px_rgba(0,0,0,0.05)] transition-all duration-200 ease-out hover:border-[#6FA8DC] hover:bg-[#EEF5FC] hover:text-[#6FA8DC] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6FA8DC]/40 focus-visible:outline-offset-2"
                  aria-label={t("header.closeDrawerAriaLabel")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav
                aria-label={t("a11y.navPrimary")}
                dir={uiLocale === "he" ? "rtl" : "ltr"}
                className="relative z-[1] min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-5"
              >
                <div className="flex flex-col gap-2">
                  <DrawerNavRow icon={<DrawerMenuIconSparkle />} label={t("home.seeTheMagic")} onActivate={drawerSeeMagic} />
                  <DrawerNavRow icon={<DrawerMenuIconFlow />} label={t("header.howItWorks")} onActivate={() => drawerGoToSection("how-it-works")} />
                  <DrawerNavRow icon={<DrawerMenuIconRocket />} label={t("home.storyTypesTitle")} onActivate={() => drawerGoToSection("story-types")} />
                  <DrawerNavRow icon={<DrawerMenuIconPictureFrame />} label={t("home.differentStylesTitle")} onActivate={() => drawerGoToSection("different-styles")} />
                  <DrawerNavRow icon={<DrawerMenuIconHeart />} label={t("home.featuresTitle")} onActivate={() => drawerGoToSection("made-for-your-family")} />
                </div>
              </nav>
              <div
                dir={uiLocale === "he" ? "rtl" : "ltr"}
                className="relative z-[1] shrink-0 border-t border-[#E6E0D8]/80 bg-white/25 px-4 py-4 backdrop-blur-[2px]"
              >
                <a
                  href={`mailto:${t("header.supportEmail")}`}
                  dir="ltr"
                  className="block rounded-md py-2 text-center text-sm font-bold text-[#6B6B6B] transition-colors duration-200 ease-out hover:text-[#6FA8DC] sm:text-base focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6FA8DC]/40 focus-visible:outline-offset-2"
                  onClick={closeDrawerAndFocusMenu}
                >
                  {t("header.supportEmail")}
                </a>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <header
      dir="ltr"
      className={`fixed start-0 top-0 z-50 w-full border-b border-white/40 bg-slate-200/30 bg-gradient-to-b from-white/25 to-slate-100/20 backdrop-blur-[10px] supports-[backdrop-filter]:bg-slate-100/25 will-change-transform ${headerMotion} ${headerVisibility}`}
      aria-hidden={!showChrome}
    >
      {drawer}

      <div className="flex min-h-12 w-full items-center gap-2 px-4 py-1.5 sm:min-h-14 sm:gap-3 sm:px-6 sm:py-2 md:px-10 lg:px-12">
        <nav
          aria-label={t("a11y.navPrimary")}
          className="font-nav flex w-full min-w-0 flex-nowrap items-center justify-between gap-2 sm:gap-4"
        >
          <div className="relative z-10 flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-2">
            <button
              ref={menuBtnRef}
              type="button"
              aria-expanded={navDrawerOpen}
              aria-controls={navDrawerOpen ? DRAWER_PANEL_ID : undefined}
              aria-label={t("header.menuButtonAriaLabel")}
              onClick={() => setNavDrawerOpen((o) => !o)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-brandNavy/12 bg-white/60 text-brandNavy transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-headerCta focus:ring-offset-2 sm:h-9 sm:w-9"
            >
              <span className="flex flex-col gap-1" aria-hidden>
                <span className="block h-0.5 w-4 rounded-full bg-brandNavy" />
                <span className="block h-0.5 w-4 rounded-full bg-brandNavy" />
                <span className="block h-0.5 w-4 rounded-full bg-brandNavy" />
              </span>
            </button>
            <span className="shrink-0">
              <PlatformLanguageSwitcher variant="header" compact />
            </span>
          </div>

          <div className="relative z-10 flex min-w-0 flex-nowrap items-center justify-end gap-1.5 overflow-x-auto overscroll-x-contain ps-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-3 md:gap-4 [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:bg-transparent">
            {isLoading && <span className="text-xs text-body">…</span>}

            {!isLoading && token && user?.fullName && (
              <span className="max-w-[9rem] truncate text-xs font-semibold text-heading sm:max-w-[12rem] sm:text-sm">
                {t("header.hello", { name: user.fullName })}
              </span>
            )}

            {!isLoading && !token && (
              <div className="flex items-center gap-1 text-zinc-200">
                <Link
                  to="/login"
                  dir="auto"
                  className="text-xs font-semibold sm:text-sm"
                >
                  {t("header.logIn")}
                </Link>
                <span className="text-zinc-400" aria-hidden>
                  |
                </span>
                <Link to="/signup" dir="auto" className="text-xs font-semibold sm:text-sm">
                  {t("header.signUp")}
                </Link>
              </div>
            )}

            {!isLoading && !token && (
              <Link
                to="/create/language"
                className="inline-flex h-8 min-w-0 shrink-0 items-center justify-center gap-1 rounded-full bg-headerCta px-3 text-[0.65rem] font-bold leading-none text-white transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-headerCta focus:ring-offset-1 sm:h-9 sm:gap-1.5 sm:px-5 sm:text-xs sm:focus:ring-offset-2 md:text-sm"
              >
                {t("header.startYourBookCta")}
              </Link>
            )}

            {!isLoading && token && user?.isAdmin === true && (
              <button
                type="button"
                onClick={handleAdminSubmissionsClick}
                className="text-xs font-semibold text-heading sm:text-sm"
              >
                {t("header.seeSubmissions")}
              </button>
            )}

            {!isLoading && token && user?.isAdmin !== true && (
              <button
                type="button"
                onClick={handleClearSessionClick}
                className="shrink-0 whitespace-nowrap text-xs font-semibold text-heading sm:text-sm"
              >
                {t("header.clearStory")}
              </button>
            )}

            {!isLoading && token && (
              <button
                type="button"
                onClick={handleLogout}
                className="text-xs font-semibold text-heading sm:text-sm"
              >
                {t("header.logOff")}
              </button>
            )}
          </div>
        </nav>
      </div>

      <Modal
        isOpen={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onConfirm={handleClearSessionConfirm}
        type="confirm"
        title={t("header.clearSessionTitle")}
        message={t("header.clearSessionMessage")}
        cancelText={t("header.cancel")}
        confirmText={isClearSubmitting ? t("header.clearing") : t("header.clearSessionConfirm")}
      />

      <Modal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title={t("header.couldNotClearSession")}
        message={errorModalMessage}
        type="error"
        confirmText={t("header.ok")}
      />
    </header>
  );
};

export default Header;
